import { prettyDateTime } from "@app/date";
import {
  fetchLlmGatewayKeyById,
  formatCurrency,
  selectLlmGatewayKeyById,
} from "@app/deploy";
import {
  currentMonthWindow,
  fetchLlmGatewayUsage,
  selectLlmGatewayUsage,
} from "@app/llm-gateway";
import { selectOrganizationSelectedId } from "@app/organizations";
import { useQuery, useSelector } from "@app/react";
import {
  llmGatewayKeyDetailUrl,
  llmGatewayKeyModelsUrl,
  llmGatewayKeyRequestsUrl,
  llmGatewayKeySettingsUrl,
} from "@app/routes";
import { maskedTokenPrefix } from "@app/string-utils";
import type { DeployLlmGatewayKey } from "@app/types";
import { CopyText } from "@app/ui/shared/copy";
import { useState } from "react";
import { Outlet, useParams } from "react-router-dom";
import {
  DetailPageHeaderView,
  ResourceDetailBar,
  ResourceDetailItem,
  ResourceDetailsTable,
} from "../shared";
import { AppSidebarLayout } from "./app-sidebar-layout";

const NoValue = () => <span className="text-gray-500">N/A</span>;

const KeyUser = ({
  user,
}: { user: DeployLlmGatewayKey["createdBy"] | null }) => {
  if (!user) return <NoValue />;

  return <>{user.email}</>;
};

const LlmGatewayKeyHeader = ({
  id,
  llmGatewayKey,
  isLoading,
}: {
  id: string;
  llmGatewayKey: DeployLlmGatewayKey;
  isLoading: boolean;
}) => {
  const orgId = useSelector(selectOrganizationSelectedId);
  const isRevoked = llmGatewayKey.status === "REVOKED";

  const [usageWindow] = useState(currentMonthWindow);
  useQuery(fetchLlmGatewayUsage({ orgId, llmKeyId: id, ...usageWindow }));
  const usage = useSelector((s) =>
    selectLlmGatewayUsage(s, { orgId, llmKeyId: id }),
  );

  return (
    <div className="flex flex-col gap-4">
      <ResourceDetailBar
        isLoading={isLoading}
        icon={
          <img
            src="/resource-types/logo-llm_key.png"
            className="w-[32px] h-[32px]"
            aria-label="LLM Gateway Key"
          />
        }
        nameLabel="LLM Gateway Key"
        name={llmGatewayKey.handle}
        id={llmGatewayKey.id}
        createdAt={llmGatewayKey.createdAt}
        costLabel="Spend (MTD)"
        cost={usage.id === "" ? null : usage.spend}
      />

      <ResourceDetailsTable columns={2} title="Details & Configuration">
        <ResourceDetailItem title="Token Prefix" isLoading={isLoading}>
          {llmGatewayKey.tokenPrefix ? (
            <span className="font-mono tracking-wide">
              {maskedTokenPrefix(llmGatewayKey.tokenPrefix)}
            </span>
          ) : (
            <NoValue />
          )}
        </ResourceDetailItem>
        <ResourceDetailItem title="Monthly Spend Limit" isLoading={isLoading}>
          {llmGatewayKey.spendLimit === null ? (
            <NoValue />
          ) : (
            formatCurrency(llmGatewayKey.spendLimit)
          )}
        </ResourceDetailItem>
        <ResourceDetailItem title="Created By" isLoading={isLoading}>
          <KeyUser user={llmGatewayKey.createdBy} />
        </ResourceDetailItem>
        <ResourceDetailItem title="Endpoint URL" isLoading={isLoading}>
          {llmGatewayKey.gatewayUrl ? (
            <CopyText text={llmGatewayKey.gatewayUrl} />
          ) : (
            <NoValue />
          )}
        </ResourceDetailItem>
        {isRevoked ? (
          <ResourceDetailItem title="Revoked" isLoading={isLoading}>
            {llmGatewayKey.revokedAt ? (
              prettyDateTime(llmGatewayKey.revokedAt)
            ) : (
              <NoValue />
            )}
          </ResourceDetailItem>
        ) : null}
        {isRevoked ? (
          <ResourceDetailItem title="Revoked By" isLoading={isLoading}>
            <KeyUser user={llmGatewayKey.revokedBy} />
          </ResourceDetailItem>
        ) : null}
      </ResourceDetailsTable>
    </div>
  );
};

export const LlmGatewayKeyDetailLayout = () => {
  const { id = "" } = useParams();
  const orgId = useSelector(selectOrganizationSelectedId);

  const loader = useQuery(fetchLlmGatewayKeyById({ id, orgId }));
  const llmGatewayKey = useSelector((s) => selectLlmGatewayKeyById(s, { id }));

  return (
    <AppSidebarLayout
      header={
        <DetailPageHeaderView
          {...loader}
          breadcrumbs={[]}
          titleLabel="LLM Gateway Key"
          title={llmGatewayKey.handle}
          detailsBox={
            <LlmGatewayKeyHeader
              id={id}
              llmGatewayKey={llmGatewayKey}
              isLoading={loader.isLoading}
            />
          }
          tabs={[
            { name: "Request Logs", href: llmGatewayKeyRequestsUrl(id) },
            { name: "Models", href: llmGatewayKeyModelsUrl(id) },
            { name: "Settings", href: llmGatewayKeySettingsUrl(id) },
          ]}
          lastBreadcrumbTo={llmGatewayKeyDetailUrl(id)}
        />
      }
      mainWrapperDivClassName="overflow-x-hidden"
    >
      <Outlet />
    </AppSidebarLayout>
  );
};
